import React from 'react'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'
import { Bar } from 'react-chartjs-2'
import useGlobalTheme from '../../../hooks/useGlobalTheme'

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend)

function Charts({ chartData, timePeriod }) {
	const theme = useGlobalTheme()
	const isLight = theme === 'light'

	const cardCls = isLight
		? 'bg-white rounded-2xl p-6 border border-gray-200 shadow-sm mb-8'
		: 'bg-gray-800 rounded-2xl p-6 border border-gray-700 mb-8'
	const titleCls = isLight
		? 'text-gray-900 text-lg font-semibold mb-6'
		: 'text-white text-lg font-semibold mb-6'

	if (!chartData) {
		return <div className={isLight ? 'text-gray-700' : 'text-white'}>Loading chart data...</div>
	}

	const barThickness = timePeriod === '24h' ? 40 : 120

	const tickColor = isLight ? 'rgb(75, 85, 99)' : 'rgb(156, 163, 175)'
	const axisBorderColor = isLight ? 'rgb(229, 231, 235)' : 'rgb(55, 65, 81)'
	const gridColor = isLight ? 'rgba(229, 231, 235, 0.7)' : 'rgba(75, 85, 99, 0.3)'
	const tooltipBg = isLight ? 'rgba(255, 255, 255, 0.98)' : 'rgba(17, 24, 39, 0.95)'
	const tooltipText = isLight ? 'rgb(17, 24, 39)' : 'rgb(243, 244, 246)'
	const tooltipBorder = isLight ? 'rgb(229, 231, 235)' : 'rgb(75, 85, 99)'

	const data = {
		labels: chartData.labels || [],
		datasets: [
			{
				label: 'Sent Emails',
				data: chartData.sent || [],
				brokerages: chartData.brokerages || [],
				recipients: chartData.recipients || [],
				backgroundColor: 'rgb(59, 130, 246)',
				borderColor: 'rgb(59, 130, 246)',
				borderWidth: 1,
				borderRadius: 6,
				barThickness: barThickness,
			},
		],
	}

	const options = {
		responsive: true,
		maintainAspectRatio: false,
		plugins: {
			legend: {
				display: false,
			},
			tooltip: {
				backgroundColor: tooltipBg,
				titleColor: tooltipText,
				bodyColor: tooltipText,
				padding: 16,
				borderColor: tooltipBorder,
				borderWidth: 1,
				displayColors: false,
				callbacks: {
					title: function (context) {
						return context[0].label
					},
					label: function (context) {
						const index = context.dataIndex
						const sent = context.parsed.y
						const brokerages = context.dataset.brokerages[index]
						const recipients = context.dataset.recipients[index]

						return [
							`Sent: ${sent} emails`,
							`Brokerages: ${brokerages}`,
							`Recipients: ${recipients}`,
						]
					},
				},
			},
		},
		scales: {
			x: {
				grid: {
					display: false,
				},
				ticks: {
					color: tickColor,
					font: { size: 12 },
				},
				border: {
					color: axisBorderColor,
				},
			},
			y: {
				beginAtZero: true,
				grid: {
					color: gridColor,
				},
				ticks: {
					color: tickColor,
					font: { size: 12 },
					stepSize: 10,
				},
				border: {
					color: axisBorderColor,
				},
			},
		},
	}

	const getTitle = () => {
		if (timePeriod === '24h') return 'Email Activity (Last 24 Hours)'
		if (timePeriod === '7d') return 'Email Activity (Last 7 Days)'
		return 'Email Activity'
	}

	return (
		<div className={cardCls}>
			<h3 className={titleCls}>{getTitle()}</h3>

			<div className="relative h-80">
				<Bar data={data} options={options} />
			</div>
		</div>
	)
}

export default Charts
